<aw-overlay
  #overlay
  [blur]="false"
  [solid]="true"
  [showCloseButton]="false"
  [isTopLayer]="request().renderInTopLayer ?? false"
  (hiding)="onOverlayHiding()"
>
  <article
    class="file-preview-lightbox"
    role="dialog"
    aria-modal="true"
    aria-labelledby="file-preview-lightbox-title"
    cdkTrapFocus
    [class.file-preview-lightbox--standalone]="!currentFile() || !showGallery()"
    [cdkTrapFocusAutoCapture]="false"
    [style.--zoom]="zoomFactor()"
  >
    <section class="file-preview-lightbox__header">
      <aw-action-bar-layout class="file-preview-lightbox__action-bar">
        <div leftLayout class="file-preview-lightbox__action-bar-left">
          @if (currentFile()) {
            @if (showGallery()) {
              <aw-badge
                class="file-preview-lightbox__counter-badge"
                [label]="selectedIndex() + 1 + ' / ' + items().length"
                [color]="Color.Steel"
              />
            }
          }
        </div>

        <div centerLayout class="file-preview-lightbox__header-container">
          <h3
            class="file-preview-lightbox__file-name file-preview-lightbox__bar-title aw-2-txt--night"
            id="file-preview-lightbox-title"
            tabindex="-1"
            cdkFocusInitial
            [class.aw-2-txt--truncate]="!!currentFile()"
            [title]="currentFile()?.name ?? null"
          >
            @if (currentFile(); as file) {
              {{ file.name }}
            } @else if (collectionState() === 'error') {
              {{ translations.previewError }}
            } @else {
              {{ translations.loading }}
            }
          </h3>

          @if (currentFile(); as file) {
            @if (file.createdByLabel || file.createdOn || file.sizeBytes !== undefined) {
              <p class="file-preview-lightbox__bar-subline aw-2-txt--steel aw-2-txt--truncate">
                @if (file.createdByLabel) {
                  {{ activityLogTranslations.activityList.attachments.uploadedBy }}: {{ file.createdByLabel }}
                }
                @if (file.createdOn) {
                  {{ commonTranslations.at }} {{ file.createdOn | zonedDate }}
                }
                @if (file.sizeBytes !== undefined) {
                  - {{ file.sizeBytes | fileSize }}
                }
              </p>
            }
          }
        </div>

        <article rightLayout class="file-preview-lightbox__action-bar-actions">
          @if (currentFile(); as file) {
            @if (file.previewKind === 'image') {
              <aw-action-button
                [icon]="'zoom_in'"
                [size]="Size.m"
                [type]="'border'"
                [tooltip]="commonTranslations.zoomIn"
                [ariaLabel]="commonTranslations.zoomIn"
                [disabled]="!zoomInActive()"
                (onClick)="zoomIn()"
              />
              <aw-action-button
                [icon]="'zoom_out'"
                [size]="Size.m"
                [type]="'border'"
                [tooltip]="commonTranslations.zoomOut"
                [ariaLabel]="commonTranslations.zoomOut"
                [disabled]="!zoomOutActive()"
                (onClick)="zoomOut()"
              />
              <aw-action-button
                [icon]="'fullscreen'"
                [size]="Size.m"
                [type]="'border'"
                [tooltip]="translations.fitScale"
                [ariaLabel]="translations.fitScale"
                (onClick)="scaleToFit()"
              />
            }

            @if (file.previewKind === 'text') {
              <aw-action-button
                data-test="file-preview-wrap"
                [icon]="'text-wrap'"
                [size]="Size.m"
                [type]="'border'"
                [selected]="isTextWrapped()"
                [tooltip]="textWrapTooltip()"
                [ariaLabel]="translations.wrapLines"
                [disabled]="!canToggleTextWrapping()"
                (onClick)="toggleTextWrapping()"
              />
              <aw-action-button
                data-test="file-preview-copy"
                [icon]="'copy'"
                [size]="Size.m"
                [type]="'border'"
                [tooltip]="translations.copyText"
                [ariaLabel]="translations.copyText"
                [disabled]="!isTextContentReady() || copyLoading()"
                (onClick)="copyTextContent()"
              />
            }

            <aw-action-button
              data-test="file-preview-download"
              [icon]="'file_download'"
              [size]="Size.m"
              [type]="'border'"
              [tooltip]="commonTranslations.download"
              [ariaLabel]="commonTranslations.download"
              [disabled]="downloadLoading()"
              (onClick)="downloadCurrentFile()"
            />

            @if (file.delete) {
              <aw-action-button
                data-test="file-preview-delete"
                [icon]="'delete'"
                [size]="Size.m"
                [type]="'border'"
                [tooltip]="commonTranslations.delete"
                [ariaLabel]="commonTranslations.delete"
                [disabled]="deleteLoading()"
                (onClick)="deleteCurrentFile()"
              />
            }
          }

          <aw-dismiss-button
            data-test="file-preview-close"
            [size]="Size.m"
            [buttonStyle]="'border'"
            [tooltip]="commonTranslations.close"
            (onClick)="close()"
          />
        </article>
      </aw-action-bar-layout>
    </section>

    @if (currentFile(); as file) {
      <section class="file-preview-lightbox__body">
        <article class="file-preview-lightbox__wrapper">
          @if (showGallery()) {
            <div class="file-preview-lightbox__controls file-preview-lightbox__controls--left">
              <aw-icon-button
                [disabled]="selectedIndex() === 0"
                [size]="Size.m"
                [icon]="'keyboard_arrow_left'"
                [tooltip]="commonTranslations.previous"
                [ariaLabel]="commonTranslations.previous"
                (click)="prev()"
              />
            </div>
          }

          <div
            class="file-preview-lightbox__preview"
            [class.file-preview-lightbox__preview--active]="previewActive()"
            [class.file-preview-lightbox__preview--transitioning]="isTransitioning()"
            [class.file-preview-lightbox__preview--transition-next]="transitionDirection() === 'next'"
            [class.file-preview-lightbox__preview--transition-prev]="transitionDirection() === 'prev'"
            [class.file-preview-lightbox__preview--pdf]="file.previewKind === 'pdf'"
            [class.file-preview-lightbox__preview--html]="file.previewKind === 'html'"
            [class.file-preview-lightbox__preview--text]="file.previewKind === 'text'"
            [class.file-preview-lightbox__preview--video]="file.previewKind === 'video'"
          >
            @if (isLoading()) {
              <div class="file-preview-lightbox__status" data-test="file-preview-loading" role="status">
                <aw-dots-loader [size]="Size.l" [color]="Color.Fog" />
                <span>{{ translations.loading }}</span>
              </div>
            }
            @if (previewError(); as error) {
              <div class="file-preview-lightbox__status" data-test="file-preview-error" role="status">
                <h2>{{ error === 'too-large' ? translations.tooLargeTitle : translations.previewError }}</h2>
                <p>
                  {{ error === 'too-large' ? translations.tooLargeDescription : translations.previewErrorDescription }}
                </p>
              </div>
            } @else if (file.previewKind === 'text' && textContent() !== null) {
              <div
                #textPreviewContainer
                class="file-preview-lightbox__preview-file file-preview-lightbox__preview-file--text"
              >
                <pre
                  #textPreviewContent
                  class="file-preview-lightbox__text-content"
                  data-test="file-preview-text"
                  tabindex="0"
                  [class.file-preview-lightbox__text-content--wrapped]="isTextWrapped()"
                >@if (highlightedTextContent(); as highlightedContent) {<code
                      class="file-preview-lightbox__syntax"
                      [innerHTML]="highlightedContent"
                    ></code
                  >} @else {{{ textContent() }}}</pre>
              </div>
            } @else if (previewUrl(); as url) {
              @switch (file.previewKind) {
                @case ('image') {
                  <div
                    #imagePreview
                    class="file-preview-lightbox__preview--image"
                    [style.visibility]="isLoading() ? 'hidden' : 'visible'"
                    [class.file-preview-lightbox__preview--pannable]="isPannable()"
                    [class.file-preview-lightbox__preview--dragging]="isDragging()"
                    [style.width.px]="imageDimensions()?.width"
                    [style.height.px]="imageDimensions()?.height"
                    [style.transform]="
                      'translate(calc(-50% + ' +
                      panOffset().x +
                      'px), calc(-50% + ' +
                      panOffset().y +
                      'px)) scale(' +
                      zoomFactor() +
                      ')'
                    "
                    (mousedown)="onPanStart($event)"
                    (touchstart)="onTouchStart($event)"
                  >
                    <img #previewImage class="file-preview-lightbox__image" [src]="url" [alt]="file.name" />
                  </div>
                }
                @case ('video') {
                  <video
                    controls
                    class="file-preview-lightbox__preview-video"
                    [src]="url"
                    [hidden]="videoLoading()"
                    (loadeddata)="onVideoLoadedData()"
                    (loadstart)="onVideoLoadStart()"
                    (error)="onPreviewFailed()"
                  ></video>
                  @if (videoLoading()) {
                    <div class="aw-2-pos--center-all">
                      <aw-dots-loader [size]="Size.l" [color]="Color.Fog" />
                    </div>
                  }
                }
                @case ('pdf') {
                  <div class="file-preview-lightbox__preview-file">
                    <iframe
                      class="file-preview-lightbox__pdf-iframe"
                      [src]="safePreviewResourceUrl()"
                      [title]="file.name"
                      (error)="onPreviewFailed()"
                    ></iframe>
                  </div>
                }
                @case ('html') {
                  <div class="file-preview-lightbox__preview-file">
                    <iframe
                      class="file-preview-lightbox__html-iframe"
                      data-test="file-preview-html"
                      referrerpolicy="no-referrer"
                      sandbox="allow-scripts"
                      [src]="safePreviewResourceUrl()"
                      [title]="file.name"
                    ></iframe>
                  </div>
                }
              }
            }
          </div>

          @if (showGallery()) {
            <div class="file-preview-lightbox__controls file-preview-lightbox__controls--right">
              <aw-icon-button
                [size]="Size.m"
                [icon]="'keyboard_arrow_right'"
                [tooltip]="commonTranslations.next"
                [ariaLabel]="commonTranslations.next"
                [disabled]="selectedIndex() === items().length - 1"
                (click)="next()"
              />
            </div>
          }
        </article>
      </section>

      @if (downloadFailed()) {
        <p class="file-preview-lightbox__download-error" data-test="file-preview-download-error" role="status">
          {{ translations.downloadFailed }}
        </p>
      }

      @if (showGallery()) {
        <section class="file-preview-lightbox__footer">
          <article #thumbnailsDetails class="file-preview-lightbox__thumbnails">
            @for (item of items(); track item.id; let index = $index) {
              <button
                class="file-preview-lightbox__thumbnail"
                type="button"
                [class.file-preview-lightbox__thumbnail--active]="selectedIndex() === index"
                [attr.aria-label]="item.name"
                [attr.aria-current]="selectedIndex() === index ? 'true' : null"
                (click)="selectThumbnail(index)"
              >
                <aw-file-preview-lightbox-thumbnail [item]="item" />
              </button>
            }

            @if (request().addFiles) {
              <button class="file-preview-lightbox__add-new-file" type="button" (click)="uploadElement.click()">
                <span class="aw-2-pos--center-all">+</span>
              </button>
              <input #uploadElement class="👻" type="file" multiple (change)="addNewFiles($event)" />
            }
          </article>
        </section>
      }
    } @else {
      <section class="file-preview-lightbox__body">
        <article class="file-preview-lightbox__wrapper">
          <div class="file-preview-lightbox__preview file-preview-lightbox__preview--active">
            @if (collectionState() === 'error') {
              <div class="file-preview-lightbox__status" data-test="file-preview-collection-error" role="status">
                <h2>{{ translations.previewError }}</h2>
                <p>{{ translations.previewErrorDescription }}</p>
              </div>
            } @else {
              <div class="file-preview-lightbox__status" data-test="file-preview-collection-loading" role="status">
                <aw-dots-loader [size]="Size.l" [color]="Color.Fog" />
                <span>{{ translations.loading }}</span>
              </div>
            }
          </div>
        </article>
      </section>
    }
  </article>
</aw-overlay>
